import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  User,
  LogOut,
  LogIn,
  UserPlus,
  Ticket,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import LanguageSwitcher from "../LanguageSwitcher";

// Social Icons
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const accountMenuRefDesktop = useRef<HTMLDivElement>(null);
  const accountMenuRefMobile = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, logout } = useAuth();
  const { t } = useLanguage();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close account menu when clicking outside (desktop or mobile dropdown)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideDesktop = accountMenuRefDesktop.current?.contains(target);
      const insideMobile = accountMenuRefMobile.current?.contains(target);

      if (!insideDesktop && !insideMobile) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsAccountMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Check if user is admin
  const isAdmin = userProfile?.ruolo === "admin";
  const isCollaboratore = userProfile?.ruolo === "collaboratore";

  const navLinks = [
    { path: "/", label: t.nav.home },
    { path: "/despre-noi", label: t.nav.about },
    { path: "/contact", label: t.nav.contact },
    { path: "/acces", label: t.nav.tickets },
    // Show Admin for admin and collaboratore users
    ...(isAdmin || isCollaboratore
      ? [
          { path: "/admin/export", label: t.nav.admin },
          { path: "/admin/scanner", label: "Scanner" },
        ]
      : []),
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        isScrolled ? "shadow-lg" : ""
      }`}
    >
      {/* Romanian flag stripe */}
      <div className="flag-stripe" />

      <div className="container-custom">
        <div className="flex items-center h-16 md:h-20 gap-4 lg:gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="relative">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden shadow-md group-hover:shadow-lg transition-shadow">
                <img
                  src="/logo.png"
                  alt="Ca Pe Vremuri Logo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="flex flex-col">
              <span className="font-display font-bold text-lg md:text-xl leading-tight text-romanian-blue">
                CaPeVremuri
              </span>
              <span className="text-xs hidden md:block text-gray-500">
                {t.header.festivalSubtitle}
              </span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-3">
            {/* Main Navigation */}
            <nav className="flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                    isActive(link.path)
                      ? "bg-romanian-blue text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Social Icons */}
            <div className="flex items-center space-x-1 border-l border-gray-200 pl-3">
              <a
                href="https://wa.me/393935817177"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full transition-all duration-200 text-gray-600 hover:bg-green-100 hover:text-green-600"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon />
              </a>
              <a
                href="https://www.tiktok.com/@ca_pe_vremuri"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full transition-all duration-200 text-gray-600 hover:bg-gray-100 hover:text-black"
                aria-label="TikTok"
              >
                <TikTokIcon />
              </a>
              <a
                href="https://www.instagram.com/ca_pe_vremuri/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full transition-all duration-200 text-gray-600 hover:bg-pink-100 hover:text-pink-600"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
            </div>

            {/* Language & Account */}
            <div className="flex items-center space-x-2">
              <LanguageSwitcher variant="header" />
              {/* Account Dropdown */}
              <div className="relative" ref={accountMenuRefDesktop}>
                <button
                  onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                  className="flex items-center space-x-1 p-2 rounded-lg transition-all duration-200 text-gray-700 hover:bg-gray-100"
                  aria-label="Account menu"
                >
                  <User className="w-5 h-5" />
                  {currentUser && userProfile && (
                    <span className="hidden xl:inline text-sm font-medium max-w-24 truncate">
                      {userProfile.nome}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isAccountMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {/* Dropdown Menu */}
                {isAccountMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-slide-down">
                    {currentUser ? (
                      <>
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-900">
                            {userProfile?.nome} {userProfile?.cognome}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {currentUser.email}
                          </p>
                        </div>
                        <Link
                          to="/acces"
                          className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Ticket className="w-4 h-4" />
                          <span>{t.nav.myTickets}</span>
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-3 px-4 py-2 text-sm text-romanian-red hover:bg-red-50 transition-colors w-full text-left"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>{t.nav.logout}</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <LogIn className="w-4 h-4" />
                          <span>{t.nav.login}</span>
                        </Link>
                        <Link
                          to="/register"
                          className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <UserPlus className="w-4 h-4" />
                          <span>{t.nav.register}</span>
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Menu & Actions */}
          <div className="flex lg:hidden items-center space-x-2">
            <LanguageSwitcher variant="header" />
            {/* Account Dropdown */}
            <div className="relative" ref={accountMenuRefMobile}>
              <button
                onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                className="flex items-center space-x-1 p-2 rounded-lg transition-all duration-200 text-gray-700 hover:bg-gray-100"
                aria-label="Account menu"
              >
                <User className="w-5 h-5" />
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isAccountMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isAccountMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-slide-down">
                  {currentUser ? (
                    <>
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">
                          {userProfile?.nome} {userProfile?.cognome}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {currentUser.email}
                        </p>
                      </div>
                      <Link
                        to="/acces"
                        className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Ticket className="w-4 h-4" />
                        <span>{t.nav.myTickets}</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-3 px-4 py-2 text-sm text-romanian-red hover:bg-red-50 transition-colors w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>{t.nav.logout}</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <LogIn className="w-4 h-4" />
                        <span>{t.nav.login}</span>
                      </Link>
                      <Link
                        to="/register"
                        className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <UserPlus className="w-4 h-4" />
                        <span>{t.nav.register}</span>
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg transition-all duration-200 text-gray-700 hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg animate-slide-down">
          <nav className="container-custom py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? "bg-romanian-blue text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Social icons in mobile menu */}
            <div className="flex items-center justify-center space-x-4 pt-4 mt-4 border-t border-gray-100">
              <a
                href="https://wa.me/393935817177"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon />
              </a>
              <a
                href="https://www.tiktok.com/@ca_pe_vremuri"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                aria-label="TikTok"
              >
                <TikTokIcon />
              </a>
              <a
                href="https://www.instagram.com/ca_pe_vremuri/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-pink-100 text-pink-600 hover:bg-pink-200 transition-colors"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
            </div>

            {/* Language Switcher in mobile menu */}
            <div className="flex justify-center pt-4 mt-4 border-t border-gray-100">
              <LanguageSwitcher variant="mobile" />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
